// Passwordless email identification — talks to the checkout-next auth endpoints
// (metro-child/checkout-next/auth.php). Responses use WP's wp_send_json_*
// envelope: { success: bool, data: {...} }.

import { NONCE_REFUSED, readRefusal, refreshNonces } from '../utils/nonce-refresh';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};

// Actions that also exist as a REST route (adp-checkout/v1). An empty admin-ajax
// request costs 3.4-5.2s on this install — WP boots in admin mode with 63 plugins
// + 46 mu-plugins — while the same handler over REST answers in ~1.2s. That gap is
// unacceptable on the COD verification flow, where the customer is watching an OTP
// auto-submit. Both paths run the identical handler and return the identical
// envelope, so admin-ajax stays the fallback (route blocked, WAF, permalinks).
// Keep in sync with cn_rest_route_map() in checkout-next/rest-routes.php.
// Anything absent here simply stays on admin-ajax.
const REST_ROUTE = {
  cn_pv_state: 'phone-state',
  cn_pv_send: 'phone-send',
  cn_pv_verify: 'phone-verify',
  // The hot ones: fired on every address edit, shipping recalc, payment switch
  // and quantity change — 5-10 times per order.
  cn_recalc_shipping: 'recalc-shipping',
  cn_set_payment: 'set-payment',
  cn_set_qty: 'set-qty',
  cn_set_giftwrap: 'set-giftwrap',
  cn_island_state: 'island-state',
  cn_save_address: 'save-address',
  // cn_place_order stays on admin-ajax until a supervised test order validates it
  // — it creates the real order and runs the gateway.
};

async function send(url, body, extraHeaders) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...(extraHeaders || {}) },
    body,
  });
  // A tab that outlived its nonces: say so, so post() can refresh them and retry.
  if (res.status === 403 && await readRefusal(res)) return NONCE_REFUSED;
  // 400 is a real handler response (validation) — anything else means the route
  // didn't serve us, so fall back rather than surfacing it as an error.
  if (!res.ok && res.status >= 400 && res.status !== 400 && res.status !== 429 && res.status !== 502) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json();
}

async function post(action, params, isRetry) {
  const body = new URLSearchParams();
  body.set('action', action);
  body.set('nonce', cfg.nonce || '');
  Object.keys(params).forEach((k) => body.set(k, params[k] == null ? '' : params[k]));
  const payload = body.toString();

  // Only take the REST path with a wp_rest nonce: without one WP treats a
  // cookie-authenticated call as logged out, so a signed-in shopper would be
  // verifying against a guest session.
  const route = cfg.restUrl && cfg.restNonce && REST_ROUTE[action];
  let json = null;
  if (route) {
    try {
      json = await send(cfg.restUrl + route, payload, { 'X-WP-Nonce': cfg.restNonce });
    } catch (e) { json = null; }
  }
  // A refused REST nonce skips admin-ajax: its cn_auth nonce dates from the same page load.
  if (!json) {
    try {
      json = await send(cfg.ajaxUrl, payload);
    } catch (e) {
      return { ok: false, data: { message: 'Πρόβλημα σύνδεσης. Δοκίμασε ξανά.' } };
    }
  }
  if (json === NONCE_REFUSED) {
    if (!isRetry && await refreshNonces(cfg)) return post(action, params, true);
    return { ok: false, data: { message: 'Πρόβλημα σύνδεσης. Δοκίμασε ξανά.' } };
  }
  return { ok: !!(json && json.success), data: (json && json.data) || {} };
}

export const identifierLookup = (identifier) => post('cn_email_lookup', { identifier });
export const sendCode = (identifier) => post('cn_send_code', { identifier });
export const passwordLogin = (identifier, password) => post('cn_password_login', { identifier, password });
export const verifyCode = (identifier, code) => post('cn_verify_code', { identifier, code });
export const setGuest = (identifier) => post('cn_set_guest', { identifier });
export const recalcShipping = (params) => post('cn_recalc_shipping', params);
export const boxnowNearest = (params) => post('cn_boxnow_nearest', params);
export const boxnowDistance = (params) => post('cn_boxnow_distance', params);
export const setCartQty = (key, qty) => post('cn_set_qty', { key, qty });
export const saveAddress = (address) => post('cn_save_address', { address: JSON.stringify(address) });
export const setPaymentMethod = (method) => post('cn_set_payment', { method });
export const setGiftWrap = (on) => post('cn_set_giftwrap', { on: on ? 1 : 0 });
export const placeOrder = (payload) => post('cn_place_order', { payload: JSON.stringify(payload) });

// Αντικαταβολή (COD) — one-time SMS verification of the contact mobile.
export const phoneVerifyState = (phone) => post('cn_pv_state', { phone });
export const phoneVerifySend = (phone) => post('cn_pv_send', { phone });
export const phoneVerifyCheck = (phone, code) => post('cn_pv_verify', { phone, code });

// Άρθρο 39Α (AADE reverse charge).
export const tax39aState = () => post('cn_39a_state', {});
export const tax39aCheck = (afm) => post('cn_39a_check', { afm });
export const tax39aIntent = (apply) => post('cn_39a_intent', { apply: apply ? 1 : 0 });
export const tax39aOtp = (username, password, repr_afm) => post('cn_39a_otp', { username, password, repr_afm });
export const tax39aVerify = (p) => post('cn_39a_verify', p);
export const tax39aApply = (p) => post('cn_39a_apply', p);
export const tax39aBizType = (bizType) => post('cn_39a_biztype', { bizType });
// Reduced island VAT (17%).
export const islandVatState = (p) => post('cn_island_state', p);
export const tax39aRemoveDoc = (id) => post('cn_39a_remove_doc', { id });
// Upload uses multipart (FormData), so it bypasses the urlencoded `post` helper.
export async function tax39aUpload(file) {
  const fd = new FormData();
  fd.set('action', 'cn_39a_upload');
  fd.set('nonce', cfg.nonce || '');
  fd.set('file', file);
  try {
    const res = await fetch(cfg.ajaxUrl, { method: 'POST', credentials: 'same-origin', body: fd });
    const json = await res.json();
    return { ok: !!(json && json.success), data: (json && json.data) || {} };
  } catch (e) {
    return { ok: false, data: { message: 'Αποτυχία μεταφόρτωσης.' } };
  }
}
export const stripeStart = (payload) => post('cn_stripe_start', { payload: JSON.stringify(payload) });
export const stripeConfirm = (orderId) => post('cn_stripe_confirm', { order_id: orderId });
